import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Loader, Download, Presentation, FileDown, Eye, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { PreviewVariablesSelector } from "./PreviewVariablesSelector";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { VariablesPreview } from "./VariablesPreview";
import { useProject } from "@/hooks/useProject";
import { useVariableMappings } from "@/hooks/useVariableMappings";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";

interface TemplatePreviewProps {
  templateId?: number;
  jotformFormId: string | null;
  jotformFormIdReferral?: string | null;
  pptFilePath?: string | null;
  canvaTemplateUrl?: string | null;
}

// Stub temporal para la función de integración con Canva
export async function generateCanvaReport({ submissionId, templateId, variables }: { submissionId: number, templateId: number, variables: any }): Promise<string> {
  // Aquí irá la integración real con Canva
  // Por ahora, retorna un enlace de ejemplo
  return Promise.resolve('https://www.canva.com/design/EXAMPLE_ID/view');
}

export function TemplatePreview({
  templateId,
  jotformFormId,
  jotformFormIdReferral,
  pptFilePath,
  canvaTemplateUrl
}: TemplatePreviewProps) {
  const { projectClient } = useProject();
  const [isDownloading, setIsDownloading] = useState(false);
  const [isRendering, setIsRendering] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState<any>(null);
  const [selectedReferralIds, setSelectedReferralIds] = useState<number[]>([]);
  const [activeTab, setActiveTab] = useState<string>("summary");
  const [isGeneratingCanva, setIsGeneratingCanva] = useState(false);
  const [canvaUrl, setCanvaUrl] = useState<string | null>(null);
  const [canvaError, setCanvaError] = useState<string | null>(null);

  // Fetch variable mappings
  const { mappings, isLoading: isLoadingMappings } = useVariableMappings(templateId || null);

  // Fetch chart configurations
  const { data: charts = [], isLoading: isLoadingCharts } = useQuery({
    queryKey: ["template_charts", templateId],
    queryFn: async () => {
      if (!templateId) return [];
      const { data, error } = await projectClient
        .from("template_charts")
        .select("*")
        .eq("template_id", templateId)
        .eq("project_id", projectClient.getProjectId())
        .eq("is_active", true)
        .order("display_order");

      if (error) throw error;
      return data || [];
    },
    enabled: !!templateId,
  });

  // Fetch referrals for selected submission
  const { data: referrals = [] } = useQuery({
    queryKey: ['submission-referrals', selectedSubmission?.db_id],
    queryFn: async () => {
      if (!selectedSubmission?.db_id) return [];

      const { data, error } = await projectClient
        .from('jotform_submissions')
        .select('*')
        .eq('parent_submission_id', selectedSubmission.db_id)
        .eq('is_deleted_in_jotform', false)
        .order('jotform_created_at', { ascending: false });

      if (error) {
        console.error('Error fetching referrals:', error);
        return [];
      }

      return data || [];
    },
    enabled: !!selectedSubmission?.db_id
  });

  // Auto-select all referrals when they load
  useEffect(() => {
    if (referrals.length > 0 && selectedReferralIds.length === 0) {
      setSelectedReferralIds(referrals.map(r => r.id));
    }
  }, [referrals, selectedReferralIds.length]);

  // Build variables from selected submission and referrals
  const variables: Record<string, any> = {
    answers: selectedSubmission?.raw_answers || {},
    referrals: referrals
      .filter(r => selectedReferralIds.includes(r.id))
      .map(r => ({
        answers: r.raw_answers,
        code: r.code,
        email: r.email
      })),
    referrals_count: selectedReferralIds.length
  };

  const handleSubmissionSelect = (submission: any) => {
    setSelectedSubmission(submission);
  };

  const handleDownloadPPT = async () => {
    if (!pptFilePath) {
      toast.error("No hay archivo PPT disponible para descargar");
      return;
    }

    try {
      setIsDownloading(true);
      const { data, error } = await supabase.storage
        .from('templates')
        .download(pptFilePath);
      
      if (error) throw error;
      
      // Crear un enlace para descargar el archivo
      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = pptFilePath.split('/').pop() || `template_${templateId}.pptx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success("Archivo descargado correctamente");
    } catch (error: any) {
      toast.error(`Error al descargar el archivo: ${error.message}`);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleRenderPPT = async () => {
    if (!templateId || !selectedSubmission) {
      toast.error("Necesitas seleccionar una submission para renderizar el reporte");
      return;
    }

    try {
      setIsRendering(true);
      toast.info("Renderizando plantilla PPT con arquitectura modular...");

      const { data, error } = await supabase.functions.invoke('render-report-orchestrator', {
        body: {
          submissionId: selectedSubmission.db_id,
          templateId: templateId,
          projectId: projectClient.getProjectId(),
          format: "base64"
        }
      });

      if (error) throw error;

      if (!data || !data.data) {
        throw new Error("No se recibieron datos del reporte renderizado");
      }

      // Convertir el base64 a un Blob
      const byteCharacters = atob(data.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: data.contentType });

      // Crear un enlace para descargar el archivo
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = data.fileName || `report_${selectedSubmission.id}.pptx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success("Reporte renderizado y descargado correctamente");
    } catch (error: any) {
      console.error("Error rendering PPT:", error);
      toast.error(`Error al renderizar el reporte: ${error.message}`);
    } finally {
      setIsRendering(false);
    }
  };


  const handleGenerateCanva = async () => {
    if (!templateId || !selectedSubmission) {
      toast.error("Necesitas seleccionar una submission para renderizar el reporte");
      return;
    }
    setIsGeneratingCanva(true);
    setCanvaError(null);
    try {
      // Aquí se debe implementar la lógica real de integración con Canva
      // Por ahora, se asume que existe una función auxiliar generateCanvaReport
      // que retorna el enlace generado
      const url = await generateCanvaReport({
        submissionId: selectedSubmission.db_id,
        templateId,
        variables: variables
      });
      setCanvaUrl(url);
      toast.success("Informe generado en Canva correctamente");
    } catch (err: any) {
      setCanvaError(err.message || 'Error desconocido');
      toast.error(`Error al generar el informe en Canva: ${err.message}`);
    } finally {
      setIsGeneratingCanva(false);
    }
  };

  // Determinar el tipo de plantilla activa
  const isCanva = !!canvaTemplateUrl;
  const isPPT = !!pptFilePath && !isCanva;

  if (!isCanva && !isPPT) {
    return (
      <div className="p-8 text-center text-red-500">
        No hay ninguna plantilla configurada para este reporte. Por favor, edita la plantilla y agrega un archivo PPTX o un enlace de Canva.
      </div>
    );
  }

  // Encabezado y acción principal
  return (
    <Card className="w-full">
      <div className="p-4 space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            {isCanva ? (
              <>
                <div className="text-lg font-semibold mb-1">Plantilla activa: <span className="text-primary">Canva</span></div>
                <a
                  href={canvaTemplateUrl!}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline text-sm"
                >
                  Ver plantilla base de Canva
                </a>
              </>
            ) : (
              <>
                <div className="text-lg font-semibold mb-1">Plantilla activa: <span className="text-primary">PPTX</span></div>
                <div className="text-sm text-muted-foreground">{pptFilePath!.split('/').pop()}</div>
              </>
            )}
          </div>
          <div className="flex gap-2">
            {isCanva ? (
              <Button
                onClick={handleGenerateCanva}
                disabled={isGeneratingCanva || !selectedSubmission}
                variant="default"
              >
                {isGeneratingCanva ? (
                  <><Loader className="h-4 w-4 mr-2 animate-spin" /> Generando en Canva...</>
                ) : (
                  <><FileDown className="h-4 w-4 mr-2" /> Generar en Canva</>
                )}
              </Button>
            ) : (
              <>
                <Button
                  onClick={handleRenderPPT}
                  disabled={isRendering || !selectedSubmission}
                  variant="default"
                >
                  {isRendering ? (
                    <><Loader className="h-4 w-4 mr-2 animate-spin" /> Renderizando...</>
                  ) : (
                    <><FileDown className="h-4 w-4 mr-2" /> Renderizar y descargar</>
                  )}
                </Button>
              </>
            )}
          </div>
        </div>
        {/* Selector de submission */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <PreviewVariablesSelector
            onSubmissionSelect={handleSubmissionSelect}
            onReferralSelectionChange={setSelectedReferralIds}
            selectedSubmission={selectedSubmission}
            selectedReferralIds={selectedReferralIds}
            jotformFormId={jotformFormId}
          />
        </div>


        {/* Mensaje de error o link generado en Canva */}
        {isCanva && (
          <div className="mt-4 flex flex-col items-center">
            {canvaError && (
              <div className="text-red-500 text-xs mb-2">{canvaError}</div>
            )}
            {canvaUrl && (
              <a
                href={canvaUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline mt-2"
              >
                Ver presentación generada en Canva
              </a>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
